import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Maximize, Clock, Sun, Moon } from 'lucide-react';
import { useTvFeed } from '../../api/tv';
import Timeline from '../planning/Timeline';
import TvCard from './TvCard';
import { fitPageSize, paginate, sortByStartDate, type RotationCard } from './rotation';
import type { TvFeed } from '../../api/types';

const POLL_MS = 60_000;
const MIN_CARD_WIDTH = 340;

/**
 * Vue kiosque TV d'atelier (lot 6). Autonome, sans interaction :
 * - Timeline proportionnelle en haut (~1/3), filtrée au périmètre de l'écran ;
 * - cartes de fournées en bas (~2/3), en roulement priorisé ;
 * - tampon anti-reflow : une nouvelle donnée n'est appliquée qu'au changement
 *   de page, jamais en pleine lecture (plan section 1.9).
 */
export default function TvView() {
  const token = new URLSearchParams(window.location.search).get('k');
  const { data: feed, error } = useTvFeed(token, POLL_MS);

  // Thème : celui de l'écran (configuré dans l'admin), avec bascule locale
  // possible pour un aperçu rapide sur le poste.
  const [darkOverride, setDarkOverride] = useState<boolean | null>(null);

  // Tampon anti-reflow : `snapshot` est la donnée réellement affichée ; elle
  // n'est remplacée qu'au retour en page 1.
  const [snapshot, setSnapshot] = useState<TvFeed | null>(null);
  const latest = useRef<TvFeed | null>(null);
  latest.current = feed ?? latest.current;
  useEffect(() => {
    if (feed && !snapshot) setSnapshot(feed);
  }, [feed, snapshot]);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Largeur de la zone cartes pour le repli automatique de la taille de page.
  const cardsRef = useRef<HTMLDivElement>(null);
  const [cardsWidth, setCardsWidth] = useState(0);
  useLayoutEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    const update = () => setCardsWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dwellMs = snapshot?.display.dwellMs ?? 15000;
  const desiredPageSize = snapshot?.display.pageSize ?? 4;
  const pageSize = fitPageSize(desiredPageSize, cardsWidth, MIN_CARD_WIDTH);

  // Les cartes suivent l'ordre chronologique (date de début), pour s'aligner
  // avec l'axe de la timeline.
  const cards: RotationCard[] = useMemo(() => {
    if (!snapshot) return [];
    return sortByStartDate(
      snapshot.details.map((d) => {
        const b = snapshot.batches.find((x) => x.id === d.id);
        return { ...d, pinned: b?.pinned ?? false };
      }),
    );
  }, [snapshot]);

  const pages = useMemo(() => paginate(cards, pageSize), [cards, pageSize]);
  const [pageIndex, setPageIndex] = useState(0);

  // Roulement : on avance de page toutes les `dwellMs`. Au retour en page 1, on
  // applique la dernière donnée reçue (jamais en cours de page).
  useEffect(() => {
    if (pages.length <= 1) {
      // Une seule page : pas de reflow de pagination, on rafraîchit doucement.
      const t = setInterval(() => {
        if (latest.current) setSnapshot(latest.current);
      }, dwellMs);
      return () => clearInterval(t);
    }
    const t = setInterval(() => {
      setPageIndex((i) => {
        const next = (i + 1) % pages.length;
        if (next === 0 && latest.current) setSnapshot(latest.current);
        return next;
      });
    }, dwellMs);
    return () => clearInterval(t);
  }, [pages.length, dwellMs]);

  // Si la pagination rétrécit, garder l'index dans les bornes.
  useEffect(() => {
    if (pageIndex >= pages.length) setPageIndex(0);
  }, [pages.length, pageIndex]);

  function goFullscreen() {
    document.documentElement.requestFullscreen?.().catch(() => undefined);
  }

  if (!token) {
    return (
      <FullScreenMessage title="Écran non configuré">
        Ouvrez l'URL kiosque fournie par l'administration (avec son jeton), ou scannez le QR code de l'écran.
      </FullScreenMessage>
    );
  }
  if (error) {
    return <FullScreenMessage title="Écran indisponible">Jeton invalide ou serveur injoignable. Contactez l'administration.</FullScreenMessage>;
  }
  if (!snapshot) {
    return <FullScreenMessage title="Chargement…">Récupération du planning…</FullScreenMessage>;
  }

  const currentPage = pages[Math.min(pageIndex, pages.length - 1)] ?? [];
  const staleness = feed ? 'à jour' : '…';

  // Badge (numéro d'ordre dans la page) par fournée affichée : lien clair entre
  // le bloc de la timeline et sa carte de détail.
  const badges: Record<number, string> = {};
  currentPage.forEach((card, i) => { badges[card.id] = String(i + 1); });

  const dark = darkOverride ?? snapshot.display.theme === 'dark';

  return (
    <div className={`${dark ? 'dark' : ''} h-screen w-screen flex flex-col overflow-hidden ${dark ? 'bg-gray-950' : 'bg-gray-100'}`}>
      {/* Bandeau */}
      <header className="bg-blue-900 dark:bg-gray-900 text-white px-6 py-2 flex items-center justify-between shrink-0 border-b border-transparent dark:border-gray-800">
        <h1 className="text-xl font-bold">PolyPlan · {snapshot.display.name}</h1>
        <div className="flex items-center gap-6 text-sm">
          <span className="flex items-center gap-2"><Clock size={16} /> {now.toLocaleTimeString('fr-FR')}</span>
          <span className="text-blue-200 dark:text-gray-400">⟳ données {staleness}</span>
          <button onClick={() => setDarkOverride(!dark)} className="text-blue-200 dark:text-gray-400 hover:text-white" title={dark ? 'Mode clair' : 'Mode sombre'}>
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button onClick={goFullscreen} className="text-blue-200 dark:text-gray-400 hover:text-white" title="Plein écran"><Maximize size={18} /></button>
        </div>
      </header>

      {/* Timeline (~1/3) — mode TV, badges liant les blocs aux cartes affichées */}
      <div className="shrink-0 p-3" style={{ height: '34vh' }}>
        <div className="h-full overflow-auto">
          <Timeline resources={snapshot.resources} batches={snapshot.batches} from={new Date(snapshot.from)} to={new Date(snapshot.to)} now={now} rowHeight={64} tvMode badges={badges} />
        </div>
      </div>

      {/* Cartes en roulement (~2/3) */}
      <div className="flex-grow px-3 pb-3 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Détail des fournées</h2>
          {pages.length > 1 && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              page {pageIndex + 1}/{pages.length}
              <span className="flex gap-1">
                {pages.map((_, i) => (
                  <span key={i} className={`w-2 h-2 rounded-full ${i === pageIndex ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`} />
                ))}
              </span>
            </div>
          )}
        </div>

        <div ref={cardsRef} className="flex-grow grid gap-3 min-h-0" style={{ gridTemplateColumns: `repeat(${pageSize}, minmax(0, 1fr))` }}>
          {currentPage.map((card, i) => (
            <TvCard key={card.id} detail={card} badge={String(i + 1)} />
          ))}
          {cards.length === 0 && (
            <div className="col-span-full flex items-center justify-center text-gray-400">Aucune fournée sur la période.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FullScreenMessage({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="h-screen w-screen bg-blue-900 text-white flex items-center justify-center p-8">
      <div className="text-center max-w-2xl">
        <h1 className="text-3xl font-bold mb-3">{title}</h1>
        <p className="text-blue-200 text-lg">{children}</p>
      </div>
    </div>
  );
}
